"use client";
/* eslint-disable @next/next/no-img-element */

import * as React from "react";
import { productMobileLcpUrl } from "@/lib/cloudflare-image-loader";

import { PeptideImagePlaceholder } from "@/ui/components/catalog/peptide-image-placeholder";
import { resolvePeptideImagePath } from "@/ui/components/catalog/peptide-image-paths";
import type { ImageCarouselImage } from "@/ui/components/ui/image-carousel";

import styles from "./pdp-layout.module.css";
import { ProductImageLightbox } from "./product-image-lightbox";

interface PeptideFallback {
	slug: string;
	code: string;
	classification: string;
}

interface ProductGalleryProps {
	images: ImageCarouselImage[];
	productName: string;
	peptideFallback?: PeptideFallback;
}

function resolveMainImage(
	images: ImageCarouselImage[],
	peptideFallback?: PeptideFallback,
): ImageCarouselImage | null {
	const selectedImage = images[0];
	if (selectedImage) return selectedImage;

	if (!peptideFallback) return null;
	const url = resolvePeptideImagePath(peptideFallback.slug);
	return url ? { url, alt: `${peptideFallback.code} · ${peptideFallback.classification}` } : null;
}

/** Renders the selected product image once; the same image is used for zoom. */
export function ProductGallery({ images, productName, peptideFallback }: ProductGalleryProps) {
	const [lightboxOpen, setLightboxOpen] = React.useState(false);
	const image = resolveMainImage(images, peptideFallback);

	if (!image) {
		return peptideFallback ? (
			<PeptideImagePlaceholder
				code={peptideFallback.code}
				className={`rounded-lg border border-border ${styles.galleryFrame}`}
			/>
		) : null;
	}

	const alt = image.alt || productName;
	const lcpSrc = productMobileLcpUrl(image.url);

	return (
		<>
			<button
				type="button"
				aria-label={productName}
				aria-haspopup="dialog"
				onClick={() => setLightboxOpen(true)}
				className={`relative block w-full text-left ${styles.galleryFrame}`}
			>
				<img
					src={lcpSrc}
					alt={alt}
					width={828}
					height={828}
					fetchPriority="high"
					decoding="async"
					className="absolute inset-0 h-full w-full object-contain"
				/>
			</button>
			<ProductImageLightbox
				open={lightboxOpen}
				onOpenChange={setLightboxOpen}
				images={[image]}
				initialIndex={0}
				productName={productName}
			/>
		</>
	);
}
